import {
  PurchaseOrderLine,
  PurchaseOrderLineFieldNamesType,
} from '../../../Lib/Types/purchase-order';

export type UpdatedField<T> = {
  currentValue: T | null | undefined;
  old_value?: T | null;
  new_value?: T | null;
  updated_at?: string;
};

export type PurchaseOrderLineWithRevision = {
  [key in keyof PurchaseOrderLine]: key extends
    | 'catalog_item'
    | 'po_line_id'
    | 'id'
    | 'carton_label_status'
    | 'total'
    | 'quantity_received'
    ? PurchaseOrderLine[key]
    : UpdatedField<NonNullable<PurchaseOrderLine[key]>>;
};

export type CleanedPurchaseOrderLine = PurchaseOrderLineWithRevision & {
  is_new_line: boolean;
  is_line_updated: boolean;
  fields_changed?: Array<keyof PurchaseOrderLineFieldNamesType>;
};

export const cleanPurchaseOrderLine = (poLine: PurchaseOrderLine) => {
  let cleanedPoLine: CleanedPurchaseOrderLine = {
    id: poLine.id,
    po_line_id: poLine.po_line_id,
    catalog_item: poLine.catalog_item,
    carton_label_status: poLine.carton_label_status,
    total: poLine.total,
    quantity_received: poLine.quantity_received,
    item_name: {
      currentValue: poLine.item_name,
    },
    vendor_style: {
      currentValue: poLine.vendor_style,
    },
    category: {
      currentValue: poLine.category,
    },
    department: {
      currentValue: poLine.department,
    },
    quantity: {
      currentValue: poLine.quantity,
    },
    upc: {
      currentValue: poLine.upc,
    },
    color_label: {
      currentValue: poLine.color_label,
    },
    price: {
      currentValue: poLine.price,
    },
    sku: {
      currentValue: poLine.sku,
    },
    size: {
      currentValue: poLine.size,
    },
    prop65: {
      currentValue: poLine.prop65,
    },
    is_new_line: false,
    is_line_updated: false,
  };

  poLine?.revision_log_line?.forEach((revision) => {
    const {
      change_type: changeType,
      field_name: fieldName,
      old_value: oldValue,
      new_value: newValue,
      updated_at: updatedAt,
    } = revision;
    type FieldMappingKey =
      | 'item_name'
      | 'upc'
      | 'vendor_style'
      | 'color_label'
      | 'quantity'
      | 'price'
      | 'sku'
      | 'size';

    if (changeType === 'update') {
      cleanedPoLine.is_line_updated = true;
      cleanedPoLine.fields_changed = cleanedPoLine.fields_changed
        ? [...cleanedPoLine.fields_changed, fieldName]
        : [fieldName];
      const key = fieldName as FieldMappingKey;
      switch (fieldName) {
        case 'upc':
        case 'vendor_style':
        case 'sku':
        case 'size':
        case 'color_label':
        case 'item_name':
          cleanedPoLine = {
            ...cleanedPoLine,
            [key]: {
              old_value: oldValue !== null ? oldValue : null,
              new_value: newValue !== null ? newValue : null,
              updated_at: updatedAt,
            },
          };
          break;
        case 'quantity':
          cleanedPoLine.quantity = {
            currentValue: poLine.quantity,
            old_value: oldValue !== null ? parseInt(oldValue, 10) : null,
            new_value: newValue !== null ? parseInt(newValue, 10) : null,
            updated_at: updatedAt,
          };
          break;
        case 'price':
          cleanedPoLine.price = {
            currentValue: poLine.price || null,
            old_value: oldValue !== null ? parseFloat(oldValue) : null,
            new_value: newValue !== null ? parseFloat(newValue) : null,
            updated_at: updatedAt,
          };
          break;
        default:
          console.warn(`Unhandled field_name: ${fieldName}`);
          break;
      }
    } else if (changeType === 'line_added') {
      cleanedPoLine.is_new_line = true;
    }
  });

  return cleanedPoLine;
};
